import { useMemo } from 'react';
import { getApiWorkflowErrorLogs } from '@/api/apiWorkflow/errorLogs';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent } from '@/store/account/selector';
import { useLocalTimezone } from '../../../../../common/hooks/useLocalTimezone';
import moment from 'moment-timezone';
import { TErrorLog } from '@/types/TApiWorkflow';

type IApiWorkflowErrorLogs = {
	workFlowForErrorLogs: { workflowName: string; forwardedFrom: string };
};

function formatTimestamp(ts: number | string, timeZone: string): string {
	const epochMs =
		typeof ts === 'number' || /^\d+$/.test(String(ts)) ? Number(ts) : Date.parse(String(ts));
	return moment(epochMs).tz(timeZone).format('ddd D MMM YY, h:mm A');
}

export const ApiWorkflowErrorLogs: React.FC<IApiWorkflowErrorLogs> = ({ workFlowForErrorLogs }) => {
	const selectedAgent = useAppSelector(getSelectedAgent);
	const agentTimeZone = selectedAgent?.time_zone;
	const { timeZone: localTimeZone } = useLocalTimezone(agentTimeZone);

	const effectiveTimeZone = useMemo(() => {
		const defaultTimezones = ['UTC', 'na'];
		if (localTimeZone && !defaultTimezones.includes(localTimeZone)) {
			return localTimeZone;
		}
		return moment.tz.guess();
	}, [localTimeZone]);

	const { data: errorLogs } = useQuery<TErrorLog[]>({
		queryKey: ['apiWorkflowErrorLogs', workFlowForErrorLogs],
		enabled: !!workFlowForErrorLogs,
		queryFn: () =>
			getApiWorkflowErrorLogs(
				workFlowForErrorLogs.forwardedFrom,
				workFlowForErrorLogs.workflowName,
			),
	});

	if (!errorLogs || errorLogs.length === 0) {
		return (
			<div className="mb-8 bg-gray-100 p-6 rounded">
				<h2 className="text-2xl font-semibold mb-4 text-gray-800">
					{workFlowForErrorLogs.workflowName}
				</h2>
				<p className="text-gray-500">No error logs available.</p>
			</div>
		);
	}

	return (
		<div className="mb-8 bg-gray-100 p-6 rounded">
			<h2 className="text-2xl font-semibold mb-4 text-gray-800">
				{workFlowForErrorLogs.workflowName}
			</h2>
			<div className="h-64 overflow-auto">
				<ul className="list-disc pl-5">
					{errorLogs.map((log, index) => (
						<li key={index} className="mb-2">
							<span>
								<strong>Time:</strong> {formatTimestamp(log.timestamp, effectiveTimeZone)}
							</span>
							<div className="text-gray-500">
								<strong>Url:</strong> {log.url}
							</div>
							<div className="text-gray-700">
								<strong>Error:</strong> {log.error}
							</div>
							{index < errorLogs.length - 1 && <hr className="my-4 border-2" />}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};
